import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { AlertTriangle, Paperclip, Loader2, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ACCEPTED_TYPES = 'image/png,image/jpeg,image/jpg,image/webp,image/heic,application/pdf';
const MAX_SIZE_MB = 8;

export function VisiteAnnulerDialog({ open, onOpenChange, visite, onSuccess }) {
    const [remarque, setRemarque] = useState('');
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef(null);

    const reset = () => {
        setRemarque('');
        setFile(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setSubmitting(false);
    };

    const handleClose = () => {
        if (submitting) return;
        reset();
        onOpenChange(false);
    };

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > MAX_SIZE_MB * 1024 * 1024) {
            toast.error(`Le fichier dépasse ${MAX_SIZE_MB} Mo`);
            e.target.value = '';
            return;
        }
        if (!ACCEPTED_TYPES.split(',').includes(f.type)) {
            toast.error('Format non supporté (PNG, JPEG, WEBP, HEIC ou PDF)');
            e.target.value = '';
            return;
        }
        setFile(f);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
    };

    const handleSubmit = async () => {
        if (!remarque.trim() || remarque.trim().length < 3) {
            toast.error('Une remarque d\'au moins 3 caractères est obligatoire.');
            return;
        }
        if (!file) {
            toast.error('Un justificatif (photo ou PDF) est obligatoire.');
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('remarque', remarque.trim());
            fd.append('justificatif', file);
            await axios.post(`${API}/api/visites/${visite.id}/annuler`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Visite annulée');
            reset();
            onOpenChange(false);
            if (onSuccess) onSuccess();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de l\'annulation');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
            <DialogContent
                className="max-w-lg"
                data-testid="visite-annuler-dialog"
                onInteractOutside={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-700">
                        <AlertTriangle size={20} className="text-red-600" />
                        Annuler la visite
                    </DialogTitle>
                    <DialogDescription>
                        Pour annuler cette visite, une <span className="font-medium text-slate-800">remarque</span> et
                        un <span className="font-medium text-slate-800">justificatif</span> (photo ou PDF) sont
                        obligatoires.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {visite && (
                        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                            <span className="font-medium text-slate-700">{visite.prospect_nom}</span>
                            {' • '}{visite.date} {visite.heure}
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <Label htmlFor="annul-remarque" className="text-sm font-medium">
                            Remarque <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                            id="annul-remarque"
                            placeholder="Motif de l'annulation (ex: client absent, urgence familiale, refus du client...)"
                            value={remarque}
                            onChange={(e) => setRemarque(e.target.value)}
                            rows={3}
                            disabled={submitting}
                            data-testid="visite-annuler-remarque"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-sm font-medium">
                            Justificatif <span className="text-red-500">*</span>
                            <span className="ml-1 text-xs text-slate-500 font-normal">
                                (PNG, JPEG, WEBP, HEIC ou PDF — max {MAX_SIZE_MB} Mo)
                            </span>
                        </Label>

                        {!file ? (
                            <button
                                type="button"
                                onClick={() => inputRef.current?.click()}
                                disabled={submitting}
                                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-[#2e7cd6] hover:bg-blue-50/50 text-slate-600 hover:text-[#2e7cd6] rounded-md px-4 py-6 transition-colors disabled:opacity-50"
                                data-testid="visite-annuler-file-trigger"
                            >
                                <Paperclip size={16} />
                                <span className="text-sm font-medium">Joindre un justificatif</span>
                            </button>
                        ) : (
                            <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <FileText size={16} className="text-slate-500 shrink-0" />
                                        <span className="text-sm text-slate-700 truncate">{file.name}</span>
                                        <span className="text-xs text-slate-400 shrink-0">
                                            ({(file.size / 1024).toFixed(0)} Ko)
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFile(null);
                                            if (previewUrl) URL.revokeObjectURL(previewUrl);
                                            setPreviewUrl(null);
                                            if (inputRef.current) inputRef.current.value = '';
                                        }}
                                        disabled={submitting}
                                        className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 disabled:opacity-50"
                                        data-testid="visite-annuler-file-remove"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                                {previewUrl && (
                                    <img
                                        src={previewUrl}
                                        alt="Aperçu justificatif"
                                        className="max-h-48 w-auto rounded border border-slate-200 mx-auto"
                                    />
                                )}
                            </div>
                        )}

                        <input
                            ref={inputRef}
                            type="file"
                            accept={ACCEPTED_TYPES}
                            onChange={handleFileChange}
                            className="hidden"
                            data-testid="visite-annuler-file-input"
                        />
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={submitting}
                        data-testid="visite-annuler-cancel"
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleSubmit}
                        disabled={submitting || !remarque.trim() || !file}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="visite-annuler-confirm"
                    >
                        {submitting ? (
                            <><Loader2 size={16} className="mr-2 animate-spin" /> Annulation...</>
                        ) : (
                            'Confirmer l\'annulation'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
